import React, { useState, useEffect, useRef } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import moment from 'moment';
import API from "../../config/api";
import { ToastContainer, toast } from "react-toastify";
import avatar from "./../../assets/assets_theme/img/avatar/1.jpg";
import CustomModalBlockUser from "../Bar/CustomModalBlockUser";
import CustomModalApproveUser from "../Bar/CustomModalApproveUser";
import CustomModalResetUser from "../Bar/CustomModalResetUser";

const UserView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [users, setUsers] = useState({
    id: null,
    custom_id: null,
    first_name: '',
    last_name: '',
    dob: null,
    email: '',
    address: '',
    gender_id: 0,
    role_id: 100,
    branch_id: 0,
    class_id: 1,
    teacher_id: 4,
    generation: null,
    phone_number: '',
    phone_number2: '',
    profile_url: avatar,
    filename: '',
    file_url: '',
    bio: '',
    test_available: 2,
  });

  const [allBranch, setAllBranch] = useState([]);
  const [dob, setDob] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fields, setFields] = useState({
    first_name: "",
    last_name: "",
    dob: "",
  });
  const [errors, setErrors] = useState({});

  // Refs
  const inputRefs = {
    first_name: useRef(null),
    last_name: useRef(null),
    dob: useRef(null),
  };

  // Constants
  const limit = 100;
  const page = 1;

  useEffect(() => {
    getAllBranch();

    const id = searchParams.get("id");
    if (id) {
      // We need to set the id in users state immediately or pass it to getOneUser
      // Since state updates are async, we'll pass it directly
      getOneUser(id);
    }

  }, [searchParams]);

  const getAllBranch = async () => {
    try {
      let query = `page=${page}&limit=${limit}`;
      let results = await API.getAllBranch(query);
      let data = results.data;
      setAllBranch(data.school_branch);
    } catch (error) {
      console.log(error);
    }
  };

  const getOneUser = async (id) => {
    try {
      let query = `user_id=${id}`;
      let results = await API.getOneUser(query);
      let data = results.data.user;

      setUsers(data);
      convertDateToString(data.dob);
      addRefValue(data);
    } catch (error) {
      console.log(error);
    }
  };

  const onProfileChange = async (event) => {
    event.preventDefault();
    let file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      let results = await API.adminUpload(formData, (percentage) => { });

      if (results) {
        setUsers(prev => ({
          ...prev,
          profile_url: results.full_url,
          file_url: results.filename
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const convertDate = (dateString) => {
    return moment(dateString, 'YYYYMMDD').format('YYYY-MM-DD');
  };

  const convertDateToString = (date) => {
    setDob(moment(date).format("YYYYMMDD"));
  };

  const addRefValue = (data) => {
    setFields(prev => ({
      ...prev,
      first_name: data.first_name,
      last_name: data.last_name,
      dob: data.dob
    }));
  };

  const validateFields = () => {
    const newErrors = {};
    let firstEmptyField = null;

    Object.keys(fields).forEach((field) => {
      const value = fields[field];
      if (typeof value !== "string" || !value.trim()) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        if (!firstEmptyField) {
          firstEmptyField = field;
        }
      }
    });

    setErrors(newErrors);

    if (firstEmptyField && inputRefs[firstEmptyField].current) {
      inputRefs[firstEmptyField].current.focus();
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleInput = (event) => {
    const { name, value } = event.target;
    setFields((prevFields) => ({
      ...prevFields,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateFields()) return;

    let data = {
      id: users.id,
      first_name: users.first_name,
      last_name: users.last_name,
      profile_url: users.file_url,
      email: users.email,
      address: users.address,
      gender_id: users.gender_id,
      role_id: users.role_id,
      phone_number: users.phone_number,
      phone_number2: users.phone_number2,
      class_id: users.class_id,
      teacher_id: users.teacher_id,
      dob: convertDate(dob),
      branch_id: users.branch_id,
      generation: users.generation,
      custom_id: users.custom_id,
      bio: users.bio,
      test_available: users.test_available,
    };

    try {
      let results = await API.postUser(data);
      if (results.result) {
        toast.success("Your action is successful", {
          data: {
            title: "Success toast",
            text: "This is a success message",
          },
        });
        setSuccess(results.result);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const blockUser = async (event) => {
    event.preventDefault();
    let data = {
      user_id: users.id,
      block: users.block ? 0 : 1,
    };
    try {
      let results = await API.postBlockUser(data);
      if (results.result) {
        getOneUser(users.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const approveUser = async (event) => {
    event.preventDefault();
    let data = {
      id: users.id,
      approve: users.approve ? 0 : 1
    };
    try {
      let results = await API.approveUser(data);
      if (results.result) {
        getOneUser(users.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const resetUser = async (event) => {
    event.preventDefault();
    let data = {
      user_id: users.id
    };
    try {
      let results = await API.resetUser(data);
      if (results.result) {
        toast.success("Your action is successful", {
          data: {
            title: "Success toast",
            text: "This is a success message",
          },
        });
        getOneUser(users.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  const generationOptions = [];
  for (let i = 0; i < 50; i++) {
    generationOptions.push(<option value={i} key={i}> {i + 1}</option>);
  }

  return (
    <div className="col-12">
      <ToastContainer />
      {success && (
        <Navigate to="/userindex" replace={true} />
      )}
      <form className="card" onSubmit={handleSubmit}>
        <h4 className="card-title fw-400">User profile</h4>
        <br />

        <div className="card-body">
          <div className="flexbox gap-items-4">
            <img className="avatar avatar-xl" src={users.profile_url ? users.profile_url : avatar} alt="..." />
            <div className="flex-grow">
              <h5>{users.first_name + ' ' + users.last_name}</h5>
              <div className="d-flex flex-column flex-sm-row gap-items-2 gap-y mt-16">
                <div className="file-group file-group-inline">
                  <button className="btn btn-sm btn-w-lg btn-outline btn-round btn-secondary file-browser" type="button">Change Picture</button>
                  <input type="file" onChange={onProfileChange} />
                </div>
              </div>
            </div>
          </div>

          <hr />

          <div className="row">
            <div className="form-group col-md-6">
              <label className="require" htmlFor="first_name">First Name</label>
              <input type="text" className="form-control" id="first_name" name="first_name"
                value={users.first_name}
                ref={inputRefs.first_name}
                style={{
                  border: errors.first_name ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, first_name: val }));
                  handleInput(event);
                }}
              />
              {errors.first_name && <span style={{ color: "red" }}>{errors.first_name}</span>}
            </div>

            <div className="form-group col-md-6">
              <label>Branch</label>
              <select className="form-control" title="&#xA0;" value={users.branch_id}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, branch_id: val }));
                }}
              >
                <option value={0}> Please select branch </option>
                {allBranch.map((branch) => (
                  <option value={branch.id} key={branch.id}> {branch.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group col-md-6">
              <label className="require" htmlFor="last_name">Last Name</label>
              <input type="text" className="form-control" id="last_name" name="last_name"
                value={users.last_name}
                ref={inputRefs.last_name}
                style={{
                  border: errors.last_name ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, last_name: val }));
                  handleInput(event);
                }}
              />
              {errors.last_name && <span style={{ color: "red" }}>{errors.last_name}</span>}
            </div>
            <div className="form-group col-md-6">
              <label className="">Sub-Branch</label>
              <select className="form-control" title="&#xA0;" value={1} disabled>
                <option value={1}> Kompong Trorlach 2</option>
              </select>
            </div>
            <div className="form-group col-md-6">
              <label className="require" htmlFor="dob">Date of birth</label>
              <input type="text" name="dob"
                className="form-control" id="dob"
                value={dob || ''}
                ref={inputRefs.dob}
                style={{
                  border: errors.dob ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  setDob(event.target.value);
                  handleInput(event);
                }}
              />
              {errors.dob && <span style={{ color: "red" }}>{errors.dob}</span>}
            </div>
            <div className="form-group col-md-6">
              <label className="">Class</label>
              <select className="form-control" title="&#xA0;" value={users.class_id}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, class_id: val }));
                }}
              >
                <option value={1}> E2A</option>
                <option value={2}>E2B</option>
                <option value={3}>E2C</option>
              </select>
            </div>
            <div className="form-group col-md-6">
              <label className="">Student ID</label>
              <input className="form-control" type="number"
                value={users.custom_id || ''}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, custom_id: val }));
                }}
              />
            </div>
            <div className="form-group col-md-6">
              <label className="">Teacher</label>
              <select className="form-control" title="&#xA0;" value={users.teacher_id}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, teacher_id: val }));
                }}
              >
                <option value={1}> Vireak</option>
                <option value={2}>Vibol</option>
                <option value={3}>Bopha</option>
                <option value={4}>N/A</option>
              </select>
            </div>
            <div className="form-group col-md-6">
              <label className="r">Gender</label>
              <select className="form-control" title="&#xA0;" value={users.gender_id}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, gender_id: val }));
                }}
              >
                <option value={1}> Male</option>
                <option value={2}>Female</option>
                <option value={0}>Other</option>
              </select>
            </div>

            <div className="form-group col-md-6">
              <label className="">Role</label>
              <select className="form-control" title="&#xA0;" value={users.role_id}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, role_id: val }));
                }}
              >
                <option value={1}>Admin</option>
                <option value={10}>Teacher</option>
                <option value={100}>Student</option>
              </select>
            </div>
            <div className="form-group col-md-6">
              <label className="">Phone Number</label>
              <input className="form-control" type="number"
                value={users.phone_number}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, phone_number: val }));
                }}
              />
            </div>
            <div className="form-group col-md-6">
              <label className="">Gernation</label>
              <select className="form-control" title="&#xA0;" value={users.generation || ''}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, generation: val }));
                }}
              >
                {generationOptions}
              </select>
            </div>
            <div className="form-group col-md-6">
              <label >Email</label>
              <input className="form-control" type="email" name="email"
                value={users.email}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, email: val }));
                }}
              />
            </div>
            <div className="form-group col-md-6">
              <label className="">Test Available</label>
              <input className="form-control" type="number"
                value={users.test_available}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, test_available: val }));
                }}
              />
            </div>
            <div className="form-group col-md-6">
              <label className="">Address</label>
              <input className="form-control" type="text"
                value={users.address}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, address: val }));
                }}
              />
            </div>
            <div className="form-group col-md-6">
              <label className="">Bio</label>
              <input className="form-control" type="text"
                value={users.bio}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, bio: val }));
                }}
              />
            </div>
          </div>

          <div className="row">
          </div>
          <span>&nbsp;</span>
          <button type="button"
            title="Block"
            className={`btn ${users.block ? 'btn-danger' : 'btn-warning'}`}
            data-toggle="modal"
            data-target="#modal-center-blockuser"
          >
            {users.block ? 'Unblock' : 'Block'} <i className="fa fa-minus-square" ></i>
          </button>
          <span>&nbsp;</span>
          <button type="button"
            title="Approve"
            className={`btn ${users.approve ? 'btn-primary' : 'btn-info'}`}
            data-toggle="modal"
            data-target="#modal-center-approveuser"
          >
            {users.approve ? 'Approve' : 'Disapprove'} <i className="fa fa-minus-square"></i>
          </button>
          <span>&nbsp;</span>
          <button type="button"
            title="Reset"
            className="btn btn-danger"
            data-toggle="modal"
            data-target="#modal-center-resetuser"
          >
            Reset <i className="fa fa-refresh"></i>
          </button>

        </div>
        <footer className="card-footer text-right">
          <button className="btn btn-secondary" onClick={goBack} type="button">Cancel</button>
          <button className="btn btn-primary" type="submit">Submit</button>
        </footer>

      </form>
      {users && (
        <>
          <CustomModalBlockUser
            data={users}
            title={users.active ? "Do you really want to unblock this user?" : "Do you really want to block this user?"}
            blockUser={blockUser}
            approveUser={approveUser}
          />
          <CustomModalApproveUser
            data={users}
            title={users.approve ? "Do you really want to approve this user?" : "Do you really want to disapprove this user?"}
            approveUser={approveUser}
          />
          <CustomModalResetUser
            data={users}
            title="Do you really want to reset this user?"
            approveUser={resetUser}
          />
        </>
      )}
    </div>
  );
};

export default UserView;
